import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnInit,
  Renderer2,
  ViewChild
} from '@angular/core';
import Konva from 'konva';
import {BehaviorSubject, combineLatest, delay, map} from 'rxjs';
import {ApplicationStateService, RefineMode} from '../services/application.state.service';
import {Point, Rectangle} from '../models/utils';
import {ScenarioImageService} from '../services/scenario-image/scenario-image.service';
import {VisualizeEngine, VisualizedScenarioImage} from '@door-vision/visualisation-engine';
import {TrackingService} from '../services/tracking.service';
import {DoorCorners, ScenarioType} from "../models/scenario-image";
import {CornerPositionWithClipped, DoorRefinement} from "@door-vision/corner-refinement";
import {convertCornersToPointsArray} from "../utils/canvas.utils";
import {DoorService} from "../services/door/door.service";
import {CustomerService} from "../services/customer/customer.service";
import {isMobile} from "../utils/device.utils";
import {ImageButtonsArrangement, ImageButtonsPlacement} from "../models/customer.config";

@Component({
  template: ''
})
export abstract class AbstractRefinePanelComponent implements OnInit, AfterViewInit {

  isHidden = new BehaviorSubject(true);
  zIndex = new BehaviorSubject(2);

  @ViewChild('refineContainer')
  refineContainer: ElementRef | undefined;

  @ViewChild('refineOverlay')
  refineOverlay: ElementRef | undefined;

  @ViewChild('refineZoom')
  refineZoom: ElementRef | undefined;

  stage: Konva.Stage | undefined;
  cornerLayer: Konva.Layer | undefined;

  doorCornersLayer: Konva.Layer | undefined;

  overlay: HTMLCanvasElement | undefined;
  zoomCanvas: HTMLCanvasElement | undefined;

  primaryColor = '#000000';
  selectedColor = '#FFFFFF';

  mode: RefineMode = 'PREVIEW';

  corners: Array<DoorCorners> | undefined;

  doorCornerRefinements: Array<DoorRefinement> = []

  visualizeBounds: Rectangle | undefined;
  scenarioImage: HTMLImageElement | undefined;
  scenario: VisualizedScenarioImage | undefined;

  showZoom = false;
  isSaveInProgress = new BehaviorSubject(false);
  isSaveInProgress$ = this.isSaveInProgress.pipe(delay(150));

  allowMultipleDoors: boolean | undefined

  allowClippedCorners: boolean | undefined

  currentScenarioType$ = this.scenarioImageService.getSelected$().pipe(map(scenario => scenario?.type))

  customerConfig$ = this.customerService.getCustomerConfig$()

  public ImageButtonsArrangement = ImageButtonsArrangement
  public ImageButtonsPlacement = ImageButtonsPlacement

  constructor(
    protected renderer: Renderer2,
    protected cdRef: ChangeDetectorRef,
    protected scenarioImageService: ScenarioImageService,
    protected doorService: DoorService,
    protected applicationStateService: ApplicationStateService,
    protected visualizeEngine: VisualizeEngine,
    protected trackingService: TrackingService,
    protected customerService: CustomerService) {
  }

  async ngOnInit(): Promise<void> {

    const customerConfig = this.customerService.getCustomerConfig$().subscribe((customerConfig) => {
      this.scenarioImageService.getSelected$().subscribe((currentScenario) => {
        // Check if we are allowed to have multiple doors
        // Only if we are on a user scenario and the customer config allows it

        this.allowMultipleDoors = currentScenario && currentScenario.type === ScenarioType.USER
          && customerConfig?.allowMultipleDoors

        this.allowClippedCorners = currentScenario && currentScenario.type === ScenarioType.USER && customerConfig?.allowClippedCorners
      })
    })

    this.applicationStateService.getRefineMode().subscribe((mode: RefineMode) => {
      if (this.mode !== mode) {
        this.mode = mode;

        if (mode === 'PREVIEW_WITH_TEMP_CORNERS') {
          this.show()
          setTimeout(() => {
            this.applicationStateService.setRefineMode('PREVIEW');
          }, 5000)
          this.visualizeEngine.setRenderDoor(true);

        } else {
          if (this.mode === 'PREVIEW') {
            this.hide()
            this.visualizeEngine.setRenderDoor(true);
          } else {
            this.zIndex.next(99);
            this.isHidden.next(false);
            this.visualizeEngine.setRenderDoor(false);
          }
        }

        this.cdRef.detectChanges();
      }
      this.drawCornersIfNecessary()
    });
  }

  ngAfterViewInit(): void {
    this.stage = new Konva.Stage({
      container: 'refine-container',
      width: this.refineContainer!.nativeElement.offsetWidth,
      height: this.refineContainer!.nativeElement.offsetHeight,
    });

    this.overlay = this.refineOverlay!.nativeElement as HTMLCanvasElement;
    this.overlay.width = this.refineOverlay!.nativeElement.offsetWidth;
    this.overlay.height = this.refineOverlay!.nativeElement.offsetHeight;
    this.drawOverlay();

    this.zoomCanvas = this.refineZoom!.nativeElement as HTMLCanvasElement;

    this.primaryColor = window.getComputedStyle(this.refineContainer!.nativeElement).getPropertyValue('--customer-primary-color') || this.primaryColor;

    combineLatest([this.visualizeEngine.getCurrentScenario()]).subscribe(([scenario]: Array<VisualizedScenarioImage>) => {
      this.scenarioImage = scenario.image;
      this.scenario = scenario;
      const newBounds = scenario.boundsOnScreen;
      // const newCorners = this.visualizeEngine.convertScenarioPositionToScreenPosition(scenario.corners);

      // TODO handle multiple corners
      /*const cornersFromFirstDoor = newCorners[0]

      // only redraw layer if bounds have changed
      // TODO handle multiple corners
      const newPointsArray = this.convertCornersToPoints(cornersFromFirstDoor)
      const oldPointsArray = this.corners && this.corners.length !== 0 ? this.convertCornersToPoints(this.corners[0]) : undefined
      if (areRectanglesEqual(newBounds, this.visualizeBounds) && arePointsEqual(newPointsArray, oldPointsArray)) {
        console.log('EQUAL :(')
        //  return;
      }*/

      if (this.cornerLayer) {
        this.cornerLayer.remove();
        this.cornerLayer = undefined;
      }

      this.visualizeBounds = newBounds;

      if (this.doorCornersLayer) {
        this.doorCornersLayer.remove();
        this.doorCornersLayer = undefined;
      }

      // this.drawCornersIfNecessary(true)
      this.drawCornersIfNecessary()

      this.drawOverlay();
    });
  }

  public currentlyShowingCorners = false

  private drawCornersIfNecessary() {

    const forceDraw = this.mode === 'EDIT' || this.mode === 'PREVIEW_WITH_TEMP_CORNERS'

    const drawCorners = this.shouldDrawCorners() || forceDraw

    if (!forceDraw && this.currentlyShowingCorners === drawCorners) {
      return
    }

    if (!this.scenario) {
      return
    }

    if (drawCorners) {
      if (!this.doorCornersLayer) {
        this.corners = this.visualizeEngine.convertScenarioPositionToScreenPosition(this.scenario.corners);
        this.doorCornersLayer = this.createDoorCornersLayer(this.scenario.scenarioImage.type);
        this.stage!.add(this.doorCornersLayer);
      } else {
        this.doorCornersLayer.show()
      }
      this.currentlyShowingCorners = true
    } else {
      if (this.doorCornersLayer) {
        this.doorCornersLayer.hide()
      }
      this.currentlyShowingCorners = false
    }
  }

  /*
   // We only wanna draw the corners if:
      // - We have a user scenario or
      // - We have an example image with more then one door
   */
  private shouldDrawCorners = (): boolean => {
    const isHidden = this.isHidden.getValue()

    return !isHidden && !!this.scenario && (this.scenario.scenarioImage.type === ScenarioType.USER
      || (this.scenario.scenarioImage.type === ScenarioType.EXAMPLE && this.scenario.corners && this.scenario.corners.length > 1))
  }

  private createDoorCornersLayer(scenarioType: ScenarioType): Konva.Layer {
    const doorCornerLayer = new Konva.Layer();

    this.doorCornerRefinements = []

    const selectedIndex = this.doorService.getSelectedDoorCornersIndexValue()

    for (const doorCorner of this.corners || []) {
      const isSelected = doorCorner.index === selectedIndex

      // The doorCorners only need to be selectable if we have more then one door
      const selectable = this.corners && this.corners.length > 1

      // DoorCorners should only by editable if we have a user scenario
      const notEditable = scenarioType === ScenarioType.EXAMPLE

      // Only deleteable if we have more then one door
      const deleteable = this.allowMultipleDoors && this.corners && this.corners.length > 1

      const doorRefinement = new DoorRefinement(
        doorCornerLayer,
        doorCorner,
        isSelected,
        {
          primaryColor: this.primaryColor,
          selectable,
          notEditable,
          deleteable,
          showDeleteButtonWithoutHover: isMobile(),
          showIndex: false,
        },
        (cursor) => this.refineContainer!.nativeElement.style.cursor = cursor, {
          onCornerFocus: (doorCorners: DoorCorners, position: CornerPositionWithClipped) => {
            this.drawZoomNew(doorCorners, position)
            this.onRefine();
          },
          onUnFocus: () => this.showZoom = false,
          onPositionChanged: (doorCorners) => {
            this.drawZoomNew(doorCorners, 'topLeft')
            this.onRefine()
          },
          onCornerPositionChanged: (doorCorners, position) => {
            this.drawZoomNew(doorCorners, position)
          },
          onSelected: () => {

            const currentlySelectedDoor = this.doorService.getSelectedDoorAtCurrentIndex()

            for (const doorCornerRefinement of this.doorCornerRefinements) {
              doorCornerRefinement.setSelected(doorCornerRefinement.getIndex() === doorCorner.index)
            }
            const currentScenario = this.scenarioImageService.getSelected()
            this.doorService.selectedDoorCorners(doorCorner, currentScenario?.type)

            // Make sure that we have a selected door on the new index
            const selectedDoor = this.doorService.getSelectedDoorAtIndex(doorCorner.index)
            if (!selectedDoor && currentlySelectedDoor) {
              this.doorService.selectProductAtCurrentIndex(currentlySelectedDoor)
            }
          },
          onDeleted: () => {
            const currentScenario = this.scenarioImageService.getSelected()
            if (currentScenario) {
              currentScenario.corners = currentScenario.corners.filter(c => c.index !== doorCorner.index)
              this.scenarioImageService.selectScenario(currentScenario)
            }

            // Select the next doorCorners if the doorCorners we deleted was selected
            if (this.doorService.getSelectedDoorCornersIndexValue() === doorCorner.index) {
              const nextDoorCorners = this.corners!.find(c => c.index !== doorCorner.index)
              if (nextDoorCorners) {
                this.doorService.selectedDoorCorners(nextDoorCorners, undefined)
              }
            }
            this.onRefine()
          }
        })
      this.doorCornerRefinements.push(doorRefinement);

      doorCornerLayer.add(doorRefinement.getGroup())
      doorRefinement.afterInit()
    }

    return doorCornerLayer;
  }

  private convertCornersToPoints(doorCorners: DoorCorners): Array<Point> {
    return convertCornersToPointsArray(doorCorners)
  }

  resizeStage(): void {
    if (this.stage) {
      this.stage.setSize({
        width: 0,
        height: 0,
      });

      this.cdRef.detectChanges();

      this.stage.setSize({
        width: this.refineContainer!.nativeElement.offsetWidth,
        height: this.refineContainer!.nativeElement.offsetHeight
      });

      this.overlay!.width = this.refineOverlay!.nativeElement.offsetWidth;
      this.overlay!.height = this.refineOverlay!.nativeElement.offsetHeight;

      this.cdRef.detectChanges();

      this.drawOverlay();
    }
  }

  drawOverlay() {
    const context = this.overlay!.getContext('2d');

    if (context) {
      context.clearRect(0, 0, this.overlay!.width, this.overlay!.height);
      context.fillStyle = 'rgba(244, 244, 244, 0.9)';
      context.fillRect(0, 0, this.overlay!.width, this.overlay!.height);

      if (this.visualizeBounds) {
        context.clearRect(this.visualizeBounds.x, this.visualizeBounds.y, this.visualizeBounds.width, this.visualizeBounds.height);
      }
    }
  }

  drawZoomNew(doorCorners: DoorCorners, position: CornerPositionWithClipped) {
    const cornerPoint = doorCorners[position];
    if (!cornerPoint) {
      return;
    }

    const context = this.zoomCanvas!.getContext('2d');
    if (context) {
      const posX = this.zoomCanvas!.width / 2;
      const posY = this.zoomCanvas!.height / 2;
      const radius = this.zoomCanvas!.width / 2;
      const dotPixel = 5;
      const zoomPixel = 100;
      context.clearRect(0, 0, this.zoomCanvas!.width, this.zoomCanvas!.height);

      const [convertedCorners] = this.visualizeEngine.convertScreenPositionToScenarioPosition([doorCorners]);

      if (position === "topLeft" || position === "topRight" || position === "bottomLeft" || position === "bottomRight") {

        // Calculate the next and previous corner to also draw those lines
        const points = this.convertCornersToPoints(doorCorners)
        const cornerIndex = points.indexOf(cornerPoint);
        const prevIndex = ((4 + cornerIndex - 1) % 4) // 4+ to not have negative numbers
        const nextIndex = ((4 + cornerIndex + 1) % 4)

        const convertedDoorPoints = this.convertCornersToPoints(convertedCorners)
        const point = convertedDoorPoints[cornerIndex];
        const prevPoint = convertedDoorPoints[prevIndex];
        const nextPoint = convertedDoorPoints[nextIndex];

        context.strokeStyle = this.primaryColor;
        context.fillStyle = this.primaryColor;
        context.lineWidth = 2.5;

        context.drawImage(this.scenarioImage!, point.x - (zoomPixel / 2), point.y - (zoomPixel / 2), zoomPixel, zoomPixel,
          0, 0, this.zoomCanvas!.width, this.zoomCanvas!.height);

        // draw lines
        context.beginPath();

        context.moveTo(posX, posY);
        context.lineTo(posX + (prevPoint.x - point.x), posY + (prevPoint.y - point.y));
        context.stroke();

        context.moveTo(posX, posY);
        context.lineTo(posX + (nextPoint.x - point.x), posY + (nextPoint.y - point.y));
        context.stroke();

        context.closePath();

        // draw border
        context.beginPath();
        context.arc(posX, posY, radius, 0, Math.PI * 2, false);
        context.stroke();
        context.closePath();

        // draw dot
        context.beginPath();
        context.arc(posX, posY, dotPixel, 0, Math.PI * 2, false);
        context.fill();
        context.closePath();

        // magic: https://stackoverflow.com/questions/40499791/html5-canvas-ctx-clip-method-used-on-a-circle-leaves-a-line-underneath-the-cir
        context.globalCompositeOperation = 'destination-in';
        context.beginPath();
        context.arc(posX, posY, radius, 0, Math.PI * 2, false);
        context.fill();
        context.closePath();
        context.globalCompositeOperation = 'source-over';

      } else {

        const point = convertedCorners[position]!;

        context.drawImage(this.scenarioImage!, point.x - (zoomPixel / 2), point.y - (zoomPixel / 2), zoomPixel, zoomPixel,
          0, 0, this.zoomCanvas!.width, this.zoomCanvas!.height);

        // draw dot
        context.beginPath();
        context.arc(posX, posY, dotPixel, 0, Math.PI * 2, false);
        context.fill();
        context.closePath();
      }


    }
    this.showZoom = true;
  }

  @HostListener('window:resize')
  onResize(): void {
    this.resizeStage();
  }

  getVisualBounds(): Rectangle {
    if (!this.visualizeBounds) {
      return new Rectangle(0, 0, 0, 0);
    }
    return this.visualizeBounds;
  }

  toCSSPixel(value: number): string {
    return value + 'px';
  }

  getSaveBtnPosX(): string {
    if (!this.visualizeBounds) {
      return '0';
    }
    return (this.visualizeBounds.x + this.visualizeBounds.width) + 'px';
  }

  getSaveBtnPosY(): string {
    if (!this.visualizeBounds) {
      return '0';
    }
    return this.visualizeBounds.y + 'px';
  }

  onRefine(): void {
    this.applicationStateService.setRefineMode('EDIT');
    this.trackingService.track({event: 'refineCorners'}).then();
  }

  async hasClippedCorners(): Promise<boolean> {
    const scenarioImage = this.scenarioImageService.getSelected()
    if (!scenarioImage) {
      return false
    }

    const selectedIndex = this.doorService.getSelectedDoorCornersIndexValue()
    for (const doorCorners of scenarioImage.corners) {
      if (doorCorners.index === selectedIndex) {
        if (doorCorners.hasClippedCorners) {
          return true
        }
      }
    }

    return false
  }

  async onToggleClippedCorners(): Promise<void> {
    this.trackingService.track({event: 'toggleClippedCorners'}).then();
    const scenarioImage = this.scenarioImageService.getSelected()
    if (!scenarioImage) {
      return
    }

    const selectedIndex = this.doorService.getSelectedDoorCornersIndexValue()

    for (const doorCorners of scenarioImage.corners) {
      if (doorCorners.index === selectedIndex) {
        doorCorners.hasClippedCorners = !doorCorners.hasClippedCorners
      }
    }

    this.scenarioImageService.selectScenario(scenarioImage)
    this.onRefine()
  }

  async onAddDoor(): Promise<void> {
    this.trackingService.track({event: 'addDoor'}).then();

    const imageSize = {
      width: this.scenarioImage!.width,
      height: this.scenarioImage!.height,
    }

    const newDoorCorners = await this.scenarioImageService.addDoorToCurrentScenario({
      imageSize
    })

    const currentDoor = this.doorService.getSelectedDoorAtCurrentIndex()

    this.doorService.selectedDoorCorners(newDoorCorners.doorCorners, undefined)
    if (currentDoor) {
      this.doorService.selectProductsAtIndex([{
        products: [currentDoor],
        index: newDoorCorners.index,
      }])
    }
    this.onRefine()
  }

  async onSave(): Promise<void> {
    this.isSaveInProgress.next(true);
    const doorCornersForStorage = this.doorCornerRefinements.map((doorCornerRefinement) => doorCornerRefinement.getDoorCorners())
    await this.scenarioImageService.updateCorners(this.visualizeEngine.convertScreenPositionToScenarioPosition(doorCornersForStorage));

    this.isSaveInProgress.next(false);
    this.trackingService.track({event: 'updateCorners'}).then();
    this.applicationStateService.setRefineMode('PREVIEW');
    this.showZoom = false;
  }

  show(): void {
    if (this.isHidden.getValue()) {
      this.isHidden.next(false);
      this.zIndex.next(2);
      setTimeout(() => this.resizeStage(), 100);
    }

    this.drawCornersIfNecessary()
  }

  hide(): void {
    if (!this.isHidden.getValue() && this.mode !== 'EDIT' && this.mode !== 'PREVIEW_WITH_TEMP_CORNERS') {
      this.isHidden.next(true);
      this.zIndex.next(2);
    }
    this.drawCornersIfNecessary()
  }
}


